import { useState, useEffect } from "react";
import { Text, Loading, DataTable, Column, Badge, Tabs, TabsList, TabsTrigger, TabsContent } from "snap-design-system";
import { Mail, MailWarning, MailX, CheckIcon, X, KeyRound, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useUserCRUD } from "~/hooks/useUserCRUD";
import { useDialogActions } from "~/store/dialogStore";
import { useUserInviteList, useUserInviteListActions } from "~/store/userInviteListStore";
import { useUserData } from "~/store/userStore";
import { UserInviteResponse } from "~/types/global";
import { usePermissionCheck } from "~/components/router/PermissionGuard";
import { Permission } from "~/helpers/permissions.helper";
import { getInviteStatusLabel, getInviteStatusColorClass, getInviteStatusTextStyle, formatDateForDisplay } from "~/helpers";
import { OrganizationChangeDialog } from "./OrganizationChangeDialog";
import { LogoutMessageDialog } from "./LogoutMessageDialog";
import EmptyData from "~/components/EmptyData";
import { usePermissionManager } from "~/services/permissionManager";
import { useResetUserKey } from "~/hooks/useResetUserKey";
import { ResetUserKeyDialog } from "./security/ResetUserKeyDialog";

// ─── Invites Tab ─────────────────────────────────────────────────────────────

const getStatusIcon = (status: string | undefined) => {
  const colorClass = getInviteStatusColorClass(status);
  switch (status) {
    case "enviado": return <MailWarning className={`size-5 ${colorClass}`} />;
    case "negado": return <MailX className={`size-5 ${colorClass}`} />;
    default: return <Mail className={`size-5 ${colorClass}`} />;
  }
};

function InvitesTab() {
  const userInviteList = useUserInviteList();
  const { setUserInviteList } = useUserInviteListActions();
  const { userInviteDialogQuery, answerInviteMutation, acceptInviteMutation, invalidadeUserInvite } = useUserCRUD();
  const { openDialog } = useDialogActions();
  const { data: invitesData, isFetching } = userInviteDialogQuery("enviado");
  const userData = useUserData();
  const [isAcceptingInvite, setIsAcceptingInvite] = useState(false);
  const { checkPermission } = usePermissionCheck();
  const canAnswerInvite = checkPermission(Permission.ANSWER_INVITE);

  useEffect(() => {
    if (invitesData) setUserInviteList(invitesData);
  }, [invitesData, setUserInviteList]);

  const handleAcceptInvite = (invite: UserInviteResponse) => {
    if (!invite.invite_id) { toast.error("ID do convite não encontrado"); return; }

    const currentOrganization = userData?.organization_name;
    const inviteOrganization = invite.organization_name;

    const handleConfirm = () => {
      setIsAcceptingInvite(true);
      const loadingToast = toast.loading("Processando convite...", { description: "Você será redirecionado para a página de login." });
      acceptInviteMutation.mutate({ data: { accept_invite: true, invite_id: invite.invite_id! }, loadingToast }, {
        onSettled: () => setIsAcceptingInvite(false),
      });
    };

    const handleCancel = () => {
      openDialog({ title: "Notificações", icon: <Mail />, content: <NotificationsDialogContent />, className: "max-w-4xl" });
    };

    if (currentOrganization && inviteOrganization && currentOrganization !== inviteOrganization) {
      openDialog({
        title: "Mudança de Organização", icon: <AlertTriangle />,
        content: <OrganizationChangeDialog.Content currentOrganization={currentOrganization} newOrganization={inviteOrganization} onConfirm={handleConfirm} onCancel={handleCancel} />,
        footer: <OrganizationChangeDialog.Footer onConfirm={handleConfirm} onCancel={handleCancel} isLoading={isAcceptingInvite} />,
        className: "max-w-lg",
      });
    } else {
      openDialog({
        title: "Confirmação de aceite", icon: <AlertTriangle />,
        content: <LogoutMessageDialog.Content onConfirm={handleConfirm} onCancel={handleCancel} />,
        footer: <LogoutMessageDialog.Footer onConfirm={handleConfirm} onCancel={handleCancel} isLoading={isAcceptingInvite} />,
        className: "max-w-lg",
      });
    }
  };

  const handleRejectInvite = (invite: UserInviteResponse) => {
    if (!invite.invite_id) { toast.error("ID do convite não encontrado"); return; }
    answerInviteMutation.mutate({ accept_invite: false, invite_id: invite.invite_id }, {
      onSuccess: async () => { toast.success("Convite rejeitado com sucesso!"); await invalidadeUserInvite(); },
    });
  };

  const inviteColumns: Column<UserInviteResponse>[] = [
    {
      key: "status_invite", header: "Status", widthClass: "w-1/4 min-w-[120px]",
      render: (_, row) => (
        <div className="flex items-center gap-2">
          {getStatusIcon(row.status_invite)}
          <Text className={`${getInviteStatusTextStyle(row.status_invite)} ${getInviteStatusColorClass(row.status_invite)}`}>
            {getInviteStatusLabel(row.status_invite)}
          </Text>
        </div>
      ),
    },
    {
      key: "email_sender", header: "Enviado por", widthClass: "w-1/4 min-w-[120px]",
      render: (_, row) => <Text className={getInviteStatusTextStyle(row.status_invite)}>{row.email_sender || "N/A"}</Text>,
    },
    {
      key: "name_sender", header: "Nome", widthClass: "w-1/4 min-w-[120px]",
      render: (_, row) => <Text className={`${getInviteStatusTextStyle(row.status_invite)} capitalize truncate`}>{row.name_sender?.toLowerCase() || "N/A"}</Text>,
    },
    {
      key: "organization_name", header: "Organização", widthClass: "w-1/4 min-w-[120px]",
      render: (_, row) => <Text className={getInviteStatusTextStyle(row.status_invite)}>{row.organization_name || "N/A"}</Text>,
    },
  ];

  if (isFetching || answerInviteMutation.isPending) {
    return <div className="flex items-center justify-center p-8"><Loading size="md" /></div>;
  }

  if (!userInviteList || userInviteList.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <Mail className="size-12 text-secondary-500 mb-4" />
        <Text variant="body-md" className="font-semibold mb-2">Nenhum convite encontrado</Text>
        <Text className="text-secondary-500">Você não possui convites pendentes no momento.</Text>
      </div>
    );
  }

  return (
    <DataTable
      emptyState={<EmptyData />}
      columns={inviteColumns}
      data={userInviteList}
      actions={canAnswerInvite ? [
        { label: "Aceitar", onClick: handleAcceptInvite, icon: <CheckIcon /> },
        { label: "Rejeitar", onClick: handleRejectInvite, icon: <X /> },
      ] : []}
      keyField="invite_id"
      actionsTitle="Ações"
    />
  );
}

// ─── Password Resets Tab (admin only) ────────────────────────────────────────

interface PasswordResetRequest {
  user_id: string;
  name: string;
  email: string;
  role: string;
  requested_at: string | null;
}

interface PasswordResetsTabProps {
  onResetUser: (req: PasswordResetRequest) => void;
}

function PasswordResetsTab({ onResetUser }: PasswordResetsTabProps) {
  const { passwordResetRequestsQuery, dismissPasswordResetRequestMutation } = useUserCRUD();
  const { data: requests, isFetching } = passwordResetRequestsQuery();

  const handleDismiss = (userId: string) => {
    dismissPasswordResetRequestMutation.mutate(userId, {
      onSuccess: () => toast.success("Solicitação dispensada."),
    });
  };

  const columns: Column<PasswordResetRequest>[] = [
    {
      key: "email", header: "Usuário",
      render: (_, row) => (
        <div>
          <Text className="font-semibold">{row.email}</Text>
          <Text variant="body-sm" color="subtle" className="capitalize">{row.name?.toLowerCase() || "—"}</Text>
        </div>
      ),
    },
    {
      key: "role", header: "Perfil",
      render: (_, row) => <Text className="capitalize">{row.role}</Text>,
    },
    {
      key: "requested_at", header: "Solicitado em",
      render: (_, row) => (
        <Text variant="body-sm">{row.requested_at ? formatDateForDisplay(row.requested_at, "short") : "—"}</Text>
      ),
    },
  ];

  if (isFetching) {
    return <div className="flex items-center justify-center p-8"><Loading size="md" /></div>;
  }

  if (!requests || requests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <KeyRound className="size-12 text-secondary-500 mb-4" />
        <Text variant="body-md" className="font-semibold mb-2">Nenhuma solicitação pendente</Text>
        <Text className="text-secondary-500">Não há solicitações de reset de senha no momento.</Text>
      </div>
    );
  }

  return (
    <DataTable
      emptyState={<EmptyData />}
      columns={columns}
      data={requests}
      actions={[
        { label: "Redefinir", onClick: onResetUser, icon: <KeyRound size={14} /> },
        { label: "Dispensar", onClick: (row) => handleDismiss(row.user_id), icon: <X size={14} /> },
      ]}
      keyField="user_id"
      actionsTitle="Ações"
    />
  );
}

// ─── Main Dialog ─────────────────────────────────────────────────────────────

export function NotificationsDialogContent() {
  const { userInviteQuery, passwordResetRequestsQuery, dismissPasswordResetRequestMutation } = useUserCRUD();
  const { checkPermission } = usePermissionCheck();
  const { isAdmin, canViewPasswordResetRequests } = usePermissionManager();
  const canViewInvites = checkPermission(Permission.GET_USER_INVITE);

  const { data: invitesData } = userInviteQuery("enviado");
  const { data: resetRequests } = passwordResetRequestsQuery(isAdmin() && canViewPasswordResetRequests());
  const inviteCount = invitesData?.length || 0;
  const resetCount = resetRequests?.length || 0;

  const defaultTab = isAdmin() && resetCount > 0 && inviteCount === 0 ? "password-resets" : "invites";

  const { resetDialog, adminWrap, openResetDialog, onOpenChange } = useResetUserKey(isAdmin());

  const handleResetUser = (req: PasswordResetRequest) => {
    openResetDialog({ userId: req.user_id, userName: req.email });
  };

  return (
    <div>
      <Tabs variant="ghost" defaultValue={defaultTab}>
        <TabsList>
          {canViewInvites && (
            <TabsTrigger value="invites" className="flex items-center gap-2 bg-transparent!">
              Convites
              {inviteCount > 0 && (
                <Badge variant="accent" className="rounded-full! text-xs text-center">
                  {inviteCount}
                </Badge>
              )}
            </TabsTrigger>
          )}
          {isAdmin() && canViewPasswordResetRequests() && (
            <TabsTrigger value="password-resets" className="flex items-center gap-2 bg-transparent!">
              Reset de Senha
              {resetCount > 0 && (
                <Badge size={"small"} variant="accent" className="rounded-full!">
                  {resetCount}
                </Badge>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {canViewInvites && (
          <TabsContent value="invites">
            <div className="h-96 overflow-y-auto">
              <InvitesTab />
            </div>
          </TabsContent>
        )}
        {isAdmin() && canViewPasswordResetRequests() && (
          <TabsContent value="password-resets">
            <div className="h-96 overflow-y-auto">
              <PasswordResetsTab onResetUser={handleResetUser} />
            </div>
          </TabsContent>
        )}
      </Tabs>

      <ResetUserKeyDialog
        open={resetDialog.open}
        onOpenChange={(open) => {
          onOpenChange(open);
        }}
        targetUserId={resetDialog.userId}
        targetUserName={resetDialog.userName}
        adminWrap={adminWrap}
        onResetComplete={() => {
          if (resetDialog.userId) {
            dismissPasswordResetRequestMutation.mutate(resetDialog.userId);
          }
        }}
      />
    </div>
  );
}

export const NotificationsDialog = {
  Content: NotificationsDialogContent,
};
